<template>
  <div>
    <app-navigation />
    <main class="admin-page">
      <div class="admin-heading">
        <span class="admin-kicker">Tenant content</span>
        <h1>Content</h1>
        <p>
          Forms are derived live from the selected child site's seeded records.
          Every request targets that site's ID.
        </p>
      </div>
      <div v-if="error" class="admin-alert bad">{{ error }}</div>
      <div v-if="notice" class="admin-alert good">{{ notice }}</div>
      <section class="admin-card">
        <div class="admin-row">
          <label class="grow"
            >Managed site<select v-model="site" @change="openSite">
              <option v-for="s in sites" :key="s.id" :value="s.id">
                {{ s.name }} · {{ s.id.slice(0, 8) }}
              </option>
            </select></label
          ><button
            class="admin-secondary"
            :disabled="totalRecords > 0 || !canWrite"
            @click="seed"
          >
            Load template seed
          </button
          ><button
            v-if="canWrite"
            class="admin-secondary"
            @click="seedPanel = !seedPanel"
          >
            Manage seed data</button
          ><button
            v-if="canWrite && totalRecords"
            class="admin-danger"
            @click="removeAll"
          >
            Remove all data
          </button>
        </div>
        <p class="admin-help">
          {{
            totalRecords
              ? `${totalRecords} records present; seeding is disabled to protect edited content.`
              : "This site is empty. Seed once after its first deployment."
          }}
        </p>
      </section>
      <section v-if="seedPanel && canWrite" class="admin-card">
        <h2>Seed data</h2>
        <p>
          Upsert preserves unrelated records. Replace removes all existing data
          before importing the selected seed.
        </p>
        <textarea v-model="seedText" rows="14" spellcheck="false"></textarea>
        <div class="admin-row">
          <button class="admin-secondary" @click="importCustom(false)">
            Add or update pasted seed</button
          ><button class="admin-danger" @click="importCustom(true)">
            Replace with pasted seed</button
          ><button class="admin-danger" @click="replaceTemplateSeed">
            Replace with template seed
          </button>
        </div>
      </section>
      <section class="admin-grid">
        <aside class="admin-card">
          <h2>Collections</h2>
          <div class="admin-list">
            <button
              v-for="c in collections"
              :key="c.collection"
              class="admin-secondary"
              @click="openCollection(c.collection)"
            >
              {{ label(c.collection) }} <span>{{ c.count }}</span>
            </button>
            <p v-if="!collections.length">No collections yet.</p>
          </div>
        </aside>
        <section class="admin-card">
          <div class="admin-row">
            <h2 class="grow">
              {{ collection ? label(collection) : "Choose a collection" }}
            </h2>
            <button
              v-if="collection && canWrite"
              class="admin-primary"
              @click="newRecord"
            >
              New record
            </button>
          </div>
          <div class="admin-list">
            <article v-for="r in page.items" :key="r.key">
              <div>
                <strong>{{ title(r) }}</strong
                ><small
                  >{{ r.key }} · updated
                  {{ new Date(r.updated_at).toLocaleString() }}</small
                >
              </div>
              <button class="admin-secondary" @click="edit(r)">Open</button
              ><button v-if="canWrite" class="admin-danger" @click="remove(r)">
                Delete
              </button>
            </article>
          </div>
          <div v-if="collection" class="admin-row">
            <button
              class="admin-secondary"
              :disabled="offset === 0"
              @click="move(-limit)"
            >
              Previous</button
            ><span
              >{{ offset + 1 }}–{{ Math.min(offset + limit, page.total) }} of
              {{ page.total }}</span
            ><button
              class="admin-secondary"
              :disabled="offset + limit >= page.total"
              @click="move(limit)"
            >
              Next
            </button>
          </div>
        </section>
      </section>
      <div
        v-if="editor"
        class="admin-modal cms-modal"
        @click.self="editor = null"
      >
        <section class="admin-card cms-dialog">
          <header class="cms-dialog-head admin-row">
            <div class="grow">
              <span class="admin-kicker">{{ label(collection) }}</span>
              <h2>{{ editor.key || "New record" }}</h2>
            </div>
            <button class="admin-secondary" @click="editor = null">
              Close
            </button>
          </header>
          <div class="cms-dialog-body">
            <label
              >Record key<input v-model.trim="editor.key" :disabled="!creating"
            /></label>
            <div class="admin-form-grid">
              <label v-for="f in fields" :key="f.name"
                >{{ label(f.name)
                }}<input
                  v-if="f.type === 'boolean'"
                  v-model="editor.value[f.name]"
                  type="checkbox" /><input
                  v-else-if="f.type === 'number'"
                  v-model.number="editor.value[f.name]"
                  type="number" /><input
                  v-else-if="['date', 'datetime-local', 'url'].includes(f.type)"
                  v-model="editor.value[f.name]"
                  :type="f.type" /><textarea
                  v-else-if="f.type === 'json'"
                  :value="JSON.stringify(editor.value[f.name], null, 2)"
                  rows="5"
                  @input="setJson(f.name, $event.target.value)"
                ></textarea
                ><textarea
                  v-else-if="f.long"
                  v-model="editor.value[f.name]"
                  rows="4"
                ></textarea
                ><input v-else v-model="editor.value[f.name]" type="text"
              /></label>
            </div>
          </div>
          <footer class="cms-dialog-foot">
            <span>Saving replaces the complete record value.</span
            ><button
              v-if="canWrite"
              class="admin-primary"
              :disabled="!editor.key"
              @click="save"
            >
              Save complete record
            </button>
          </footer>
        </section>
      </div>
    </main>
  </div>
</template>
<script>
import { session, can } from "../platform/session";
import * as api from "../platform/api";
export default {
  name: "Cms",
  data() {
    return {
      root: session.root,
      sites: [],
      site: "",
      collections: [],
      collection: "",
      page: { items: [], total: 0 },
      limit: 25,
      offset: 0,
      editor: null,
      creating: false,
      seedPanel: false,
      seedText:
        '[\n  {"collection":"businesses","key":"example","value":{"name":"Example Business"}}\n]',
      error: "",
      notice: "",
    };
  },
  computed: {
    canWrite: () => can("data:write"),
    totalRecords() {
      return this.collections.reduce((n, c) => n + c.count, 0);
    },
    fields() {
      return Object.entries(this.editor?.value || {}).map(([name, value]) => ({
        name,
        type: this.type(name, value),
        long: typeof value === "string" && value.length > 100,
      }));
    },
  },
  async created() {
    this.sites = await api.children(this.root.id, this.root.id);
    this.site = this.sites[0]?.id || "";
    if (this.site) await this.openSite();
  },
  methods: {
    label(v) {
      return String(v)
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    type(name, v) {
      if (typeof v === "boolean") return "boolean";
      if (typeof v === "number") return "number";
      if (v && typeof v === "object") return "json";
      if (
        /url|image|photo|website|link/i.test(name) ||
        /^https?:\/\//.test(v || "")
      )
        return "url";
      if (/date$/i.test(name) && /^\d{4}-\d\d-\d\d$/.test(v || ""))
        return "date";
      if (
        /(_at|datetime|starts|ends)/i.test(name) &&
        /^\d{4}-\d\d-\d\dT/.test(v || "")
      )
        return "datetime-local";
      return "text";
    },
    title(r) {
      return r.value.name || r.value.title || r.value.label || r.key;
    },
    async call(fn, msg) {
      this.error = "";
      this.notice = "";
      try {
        await fn();
        if (msg) this.notice = msg;
      } catch (e) {
        if (e.status === 403) {
          this.error = "You do not have data access to this site.";
          this.collections = [];
        } else this.error = e.message;
      }
    },
    async openSite() {
      this.collection = "";
      this.page = { items: [], total: 0 };
      await this.call(async () => {
        this.collections = await api.siteCollections(this.site, this.root.id);
      }, "");
    },
    async refresh(col) {
      this.collections = await api.siteCollections(this.site, this.root.id);
      await this.openCollection(col);
    },
    async openCollection(name) {
      this.collection = name;
      this.offset = 0;
      await this.load();
    },
    async load() {
      await this.call(async () => {
        this.page = await api.siteData(
          this.site,
          this.root.id,
          this.collection,
          this.limit,
          this.offset
        );
      }, "");
    },
    move(n) {
      this.offset += n;
      return this.load();
    },
    async edit(r) {
      await this.call(async () => {
        const fresh = await api.siteRecord(
          this.site,
          this.root.id,
          this.collection,
          r.key
        );
        this.editor = {
          key: fresh.key,
          value: JSON.parse(JSON.stringify(fresh.value)),
        };
        this.creating = false;
      }, "");
    },
    newRecord() {
      this.editor = { key: "", value: { name: "", description: "" } };
      this.creating = true;
    },
    setJson(name, text) {
      try {
        this.$set(this.editor.value, name, JSON.parse(text));
        this.error = "";
      } catch {
        this.error = `${this.label(name)} must be valid JSON`;
      }
    },
    async save() {
      const col = this.collection;
      await this.call(async () => {
        await api.saveSiteRecord(
          this.site,
          this.root.id,
          col,
          this.editor.key,
          this.editor.value
        );
        this.editor = null;
        await this.refresh(col);
      }, "Record saved");
    },
    async remove(r) {
      if (!confirm(`Delete ${r.key}?`)) return;
      const col = this.collection;
      await this.call(async () => {
        await api.deleteSiteRecord(this.site, this.root.id, col, r.key);
        await this.refresh(col);
      }, "Record deleted");
    },
    async seed() {
      if (
        this.totalRecords ||
        !confirm(
          "Import this template’s bundled seed data now? Do this only before editors begin making changes."
        )
      )
        return;
      await this.call(async () => {
        const result = await api.seedSiteData(this.site, this.root.id);
        await this.openSite();
        this.notice = `${result.imported} records seeded`;
      }, "Template data seeded");
    },
    customSeedRecords() {
      const rows = JSON.parse(this.seedText);
      if (!Array.isArray(rows))
        throw new Error("Seed data must be a JSON array.");
      rows.forEach((row, index) => {
        if (
          !row?.collection ||
          !row?.key ||
          !row.value ||
          typeof row.value !== "object" ||
          Array.isArray(row.value)
        )
          throw new Error(
            `Seed record ${index + 1} must contain collection, key, and an object value.`
          );
      });
      return rows;
    },
    async clearAll() {
      const collections = await api.siteCollections(this.site, this.root.id);
      for (const summary of collections) {
        while (true) {
          const page = await api.siteData(
            this.site,
            this.root.id,
            summary.collection,
            200,
            0
          );
          if (!page.items.length) break;
          for (const record of page.items)
            await api.deleteSiteRecord(
              this.site,
              this.root.id,
              summary.collection,
              record.key
            );
        }
      }
    },
    async removeAll() {
      if (
        !confirm(
          `Delete all ${this.totalRecords} records from this site? This cannot be undone.`
        )
      )
        return;
      await this.call(async () => {
        await this.clearAll();
        await this.openSite();
      }, "All site data removed");
    },
    async importCustom(replace) {
      const rows = this.customSeedRecords();
      if (
        replace &&
        !confirm("Delete all existing data and replace it with the pasted seed?")
      )
        return;
      await this.call(async () => {
        if (replace) await this.clearAll();
        for (const row of rows)
          await api.saveSiteRecord(
            this.site,
            this.root.id,
            row.collection,
            row.key,
            row.value
          );
        await this.openSite();
      }, `${rows.length} seed records ${replace ? "replaced site data" : "upserted"}`);
    },
    async replaceTemplateSeed() {
      if (
        !confirm(
          "Delete all existing data and restore this site’s template seed?"
        )
      )
        return;
      await this.call(async () => {
        await this.clearAll();
        const result = await api.seedSiteData(this.site, this.root.id);
        await this.openSite();
        this.notice = `${result.imported} template records restored`;
      }, "Template seed restored");
    },
  },
};
</script>
<style scoped>
.cms-modal {
  overflow: hidden;
  padding: clamp(10px, 3vh, 28px);
}
.cms-dialog {
  width: min(920px, 100%) !important;
  max-height: calc(100vh - clamp(20px, 6vh, 56px));
  margin: 0 !important;
  padding: 0 !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.cms-dialog-head {
  flex: 0 0 auto;
  align-items: center;
  padding: 20px 22px 14px;
  border-bottom: 1px solid #dde5e1;
  background: #fff;
}
.cms-dialog-head h2 {
  margin: 4px 0 0;
}
.cms-dialog-body {
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 20px 22px;
  scrollbar-gutter: stable;
}
.cms-dialog-foot {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 22px;
  border-top: 1px solid #dde5e1;
  background: #f7f9f8;
}
.cms-dialog-foot span {
  font-size: 11px;
  color: #68756f;
}
@media (max-width: 800px) {
  .cms-dialog-head,
  .cms-dialog-foot {
    align-items: stretch;
  }
  .cms-dialog-foot {
    flex-direction: column;
  }
  .cms-dialog-body {
    padding: 16px;
  }
  .cms-dialog-head {
    padding: 16px;
  }
}
</style>
